// Pure derivations for the Plans tab. No React, no ambient clock.
//
// The endpoint does the counting; this module turns it into the sentences and
// severity the panel renders, and encodes the two things a reader must not be
// allowed to misunderstand:
//
// 1. `stream_active: false` means the plan-events feature flag was off for the
//    whole window — nobody's plan work was recorded. Rendering zeroes then
//    would read as "this coach wrote nothing", which is a different and
//    defamatory claim. `planState` separates the two.
// 2. "Plans handed out" counts distinct plans, so a five-weekday clone is one
//    hand-out. The copy says so, because the raw event count is ~5x higher and
//    someone will eventually compare the two.

/** What the tab should render. */
export const PLAN_STATE = {
  /** The stream recorded nothing at all — the flag was off. */
  NOT_RECORDING: "notRecording",
  /** The stream was live and this coach did no plan work. */
  NO_ACTIVITY: "noActivity",
  /** Real numbers. */
  READY: "ready",
};

/**
 * Decide which of the three states the payload represents.
 *
 * @param {Object|null|undefined} payload - `/analytics/coaches/{id}/plans` response.
 * @returns {'notRecording'|'noActivity'|'ready'}
 */
export function planState(payload) {
  if (!payload) return PLAN_STATE.NOT_RECORDING;
  if (payload.stream_active === false) return PLAN_STATE.NOT_RECORDING;
  const t = payload.totals || {};
  const touched =
    (t.plans_created || 0) +
    (t.plans_handed_out || 0) +
    (t.plans_edited || 0) +
    (t.editing_sessions || 0);
  return touched > 0 ? PLAN_STATE.READY : PLAN_STATE.NO_ACTIVITY;
}

/**
 * Copy for the "no plan work" state.
 *
 * This state is only honest if the reader is told when a filter could be hiding
 * the work. `actor_only` — which the endpoint defaults to true — counts only
 * plan work where this coach is the recorded actor. A plan handed to their
 * client by an admin, an ops user or an automated flow is recorded against that
 * actor instead, so "this coach created, handed out or edited nothing" can be
 * flatly false while the stream is recording perfectly well. Say what was
 * measured rather than what the coach supposedly didn't do.
 *
 * @param {Object|null|undefined} payload - `/plans` response.
 * @returns {{title: string, hint: string, filtered: boolean}}
 */
export function noActivityCopy(payload) {
  // The response's own echo outranks local state — the server owns the default.
  const filtered = payload?.actor_only !== false;

  if (!filtered) {
    return {
      title: "No plan work in this period",
      hint: "Plan activity was being recorded, and nobody created, handed out or edited a plan for this coach's clients. Try a wider date range.",
      filtered: false,
    };
  }

  return {
    title: "No plan work by this coach in this period",
    hint: "This counts only plans this coach worked on themselves. One handed to their client by someone else — an admin, or an automated flow — is recorded against that person instead. Widen to all plan work on their clients, or try a longer date range.",
    filtered: true,
  };
}

/** Finite number or null. */
function num(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Round to at most one decimal. */
function round1(value) {
  const n = num(value);
  return n == null ? null : Math.round(n * 10) / 10;
}

/**
 * The headline tiles, each with the sentence that says what it means.
 *
 * @param {Object|null|undefined} payload
 * @returns {Array<{id: string, label: string, value: number|null, tone: string, sentence: string|null}>}
 */
export function planHeadlines(payload) {
  const t = payload?.totals || {};
  const created = num(t.plans_created);
  const handedOut = num(t.plans_handed_out);
  const reached = num(t.clients_reached);
  const sessions = num(t.editing_sessions);
  const edited = num(t.plans_edited);
  const assigned = num(payload?.clients_assigned);

  const perClient = round1(
    reached != null && reached > 0 && handedOut != null ? handedOut / reached : null
  );
  const unreached =
    assigned != null && reached != null ? Math.max(0, assigned - reached) : null;

  return [
    {
      id: "created",
      label: "Plans written",
      value: created,
      tone: "neutral",
      sentence: created == null ? null : "Authored from scratch, not cloned.",
    },
    {
      id: "handedOut",
      label: "Plans handed out",
      value: handedOut,
      tone: "neutral",
      // A five-weekday clone emits five events but is one plan reaching a
      // client. Say so — the raw event count is visibly different.
      sentence:
        handedOut == null
          ? null
          : "Distinct plans, so a week of clones counts once." +
            (perClient != null ? ` About ${perClient} per client reached.` : ""),
    },
    {
      id: "reached",
      label: "Clients who got one",
      value: reached,
      tone: "neutral",
      sentence:
        unreached == null
          ? null
          : unreached === 0
          ? "Every assigned client got a plan in this period."
          : `${unreached} of ${assigned} assigned clients got nothing in this period.`,
    },
    {
      id: "sessions",
      label: "Editing sessions",
      value: sessions,
      tone: "neutral",
      sentence:
        sessions == null
          ? null
          : edited != null && edited > 0
          ? `Across ${edited} plan${edited === 1 ? "" : "s"}. A burst of edits counts once.`
          : "A burst of edits in one sitting counts once.",
    },
  ];
}

/**
 * Clients flagged as worth a look, with the sentence explaining why.
 *
 * @param {Object|null|undefined} payload
 * @returns {Array<Object>} Each entry gains a `reason` string.
 */
export function needsALook(payload) {
  const rows = Array.isArray(payload?.needs_a_look) ? payload.needs_a_look : [];
  const churnAt = payload?.thresholds?.churn_sessions ?? 4;
  const withinHours = payload?.thresholds?.replaced_within_hours ?? 24;

  return rows.map((row) => {
    const parts = [];
    if (row.flags?.includes("changed_too_often")) {
      parts.push(
        `${row.editing_sessions} separate editing sessions this period — more than ${churnAt}.`
      );
    }
    if (row.flags?.includes("replaced_within_a_day")) {
      const n = row.fast_replacements || 1;
      parts.push(
        `${n} plan${n === 1 ? "" : "s"} replaced inside ${withinHours}h, before the client is likely to have seen ${
          n === 1 ? "it" : "them"
        }.`
      );
    }
    return {
      ...row,
      // Severity: a plan the client never saw is worse than a busy editor.
      tone: row.flags?.includes("replaced_within_a_day") ? "danger" : "warn",
      reason: parts.join(" "),
    };
  });
}

/**
 * Widen the change distribution into chart-ready labels.
 *
 * @param {Object|null|undefined} payload
 * @returns {Array<{label: string, clients: number}>}
 */
export function changeDistribution(payload) {
  const rows = Array.isArray(payload?.change_distribution) ? payload.change_distribution : [];
  return rows.map((b) => ({
    label: b.open_ended ? `${b.changes}+` : String(b.changes),
    clients: Number(b.clients) || 0,
  }));
}
